import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { Loader2, FileText, Package as PackageIcon, Receipt, CheckCircle2, Clock, XCircle, AlertCircle, LogOut, Upload as UploadIcon, ExternalLink, Wallet, Copy, CreditCard, FolderOpen, Download, UserCog, Save, MessageCircle, Ban, ShieldCheck, BadgeCheck, Calculator, Coins, Landmark, Banknote, Stamp, Sparkles, Trash2, Undo2 } from "lucide-react";
import { softDeleteOrder, restoreOrder } from "@/lib/orders.functions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { getOrderInvoiceSignedUrl } from "@/lib/admin.functions";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { useDownloadInvoice } from "@/hooks/use-download-invoice";
import { InvoiceDownloadControl } from "@/components/invoice/InvoiceDownloadControl";
import { CompactReceiptDialog } from "@/components/invoice/CompactReceiptDialog";
import { OrderProgressBar } from "@/components/site/OrderProgressBar";
import i18n from "@/lib/i18n";
import { CommissionPayoutCard } from "@/components/site/CommissionPayoutCard";
import { CommissionHistoryTable } from "@/components/site/CommissionHistoryTable";


export const Route = createFileRoute("/dashboard")({
  head: () => ({
    meta: [
      { title: i18n.t("dashboard.metaTitle") },
      { name: "description", content: i18n.t("dashboard.metaDesc") },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/dashboard" }],
  }),
  component: DashboardPage,
});

type Order = {
  id: string; reference_id: string; service_title: string; package_name: string;
  amount: number | null; total_price?: number | null; status: string; payment_status: string;
  document_urls: string[]; created_at: string; payment_method: string | null;
  payment_transaction_id: string | null; notes: string | null;
  delivery_status?: string | null; delivery_url?: string | null;
  delivery_notes?: string | null; delivered_at?: string | null;
  user_deleted_at?: string | null;
};

function filenameFromUrl(url: string, fallback: string) {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return last ? decodeURIComponent(last) : fallback;
  } catch { return fallback; }
}

const STATUS_META: Record<string, { key: string; icon: any; cls: string }> = {
  pending: { key: "pending", icon: Clock, cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  processing: { key: "processing", icon: Loader2, cls: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  completed: { key: "completed", icon: CheckCircle2, cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  rejected: { key: "rejected", icon: XCircle, cls: "bg-red-500/15 text-red-600 border-red-500/30" },
};

function DashboardPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [profile, setProfile] = useState<any>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [rejectionMsgs, setRejectionMsgs] = useState<Record<string, { title: string; body: string; created_at: string }>>({});
  const [supportWa, setSupportWa] = useState<{ number: string; label: string } | null>(null);
  const [showDueReminder, setShowDueReminder] = useState(false);
  const [showDownloadSuccess, setShowDownloadSuccess] = useState(false);
  const [showBlockedPopup, setShowBlockedPopup] = useState(false);
  const isBlocked = !!profile?.is_blocked;
  const isB2B = userRoles.includes("b2b");
  const isVIP = isB2B && (profile?.b2b_tier === "vip");

  // Show blocked popup once per session when profile loads and user is blocked
  useEffect(() => {
    if (!isBlocked || !user) return;
    const key = `otv_blocked_shown_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    setShowBlockedPopup(true);
    sessionStorage.setItem(key, "1");
  }, [isBlocked, user]);

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => { if (!cancelled) setProfile(data); });
    supabase.from("user_roles").select("role").eq("user_id", user.id).then(({ data }) => {
      if (!cancelled) setUserRoles((data ?? []).map((r: any) => r.role));
    });

    const fetchOrders = async () => {
      const { data, error } = await supabase
        .from("orders").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      if (error) {
        console.error("[dashboard.fetchOrders] failed", {
          code: (error as any).code,
          message: error.message,
          details: (error as any).details,
          hint: (error as any).hint,
          userId: user.id,
        });
        const isPerm = /permission denied|row-level security|policy/i.test(error.message ?? "") || (error as any).code === "42501";
        toast.error(isPerm
          ? "Cannot load orders — permission denied. Try signing out and back in."
          : error.message);
        return;
      }
      if (!cancelled) setOrders((data ?? []) as Order[]);
    };
    fetchOrders();

    // Backup polling for payment_status changes (every 30s)
    const pollId = setInterval(fetchOrders, 30000);

    // Real-time updates: reflect admin status changes instantly
    const channel = supabase
      .channel(`user-orders-${user.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders", filter: `user_id=eq.${user.id}` },
        (payload) => {
          setOrders((prev) => {
            const list = prev ?? [];
            if (payload.eventType === "INSERT") {
              const row = payload.new as Order;
              if (list.some((o) => o.id === (row as any).id)) return list;
              return [row, ...list];
            }
            if (payload.eventType === "UPDATE") {
              const row = payload.new as Order;
              const next = list.map((o) => ((o as any).id === (row as any).id ? { ...o, ...row } : o));
              const prevStatus = (payload.old as any)?.status;
              const newStatus = (row as any)?.status;
              const prevPay = (payload.old as any)?.payment_status;
              const newPay = (row as any)?.payment_status;
              if (prevStatus && newStatus && prevStatus !== newStatus) {
                toast.success(`Order status updated: ${newStatus}`);
              }
              if (prevPay && newPay && prevPay !== newPay) {
                if (newPay === "verified") toast.success("✅ Payment verified!");
                else if (newPay === "rejected") toast.error("Payment was rejected — please retry.");
              }
              return next;
            }
            if (payload.eventType === "DELETE") {
              const oldId = (payload.old as any)?.id;
              return list.filter((o) => (o as any).id !== oldId);
            }
            return list;
          });
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      clearInterval(pollId);
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Fetch rejection notifications for the user's orders and subscribe to new ones
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchRejections = async () => {
      const { data } = await supabase
        .from("notifications")
        .select("order_id,title,body,created_at,type")
        .eq("user_id", user.id)
        .or("type.eq.payment_rejected,type.eq.order_rejected,title.ilike.%reject%")
        .order("created_at", { ascending: false });
      if (cancelled || !data) return;
      const map: Record<string, { title: string; body: string; created_at: string }> = {};
      for (const n of data as any[]) {
        if (!n.order_id) continue;
        if (!map[n.order_id]) map[n.order_id] = { title: n.title, body: n.body ?? "", created_at: n.created_at };
      }
      setRejectionMsgs(map);
    };
    fetchRejections();
    const ch = supabase
      .channel(`user-notif-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, (payload) => {
        const n = payload.new as any;
        if (!n?.order_id) return;
        const isReject = /reject/i.test(n.type ?? "") || /reject/i.test(n.title ?? "");
        if (!isReject) return;
        setRejectionMsgs((prev) => ({ ...prev, [n.order_id]: { title: n.title, body: n.body ?? "", created_at: n.created_at } }));
      })
      .subscribe();
    return () => { cancelled = true; supabase.removeChannel(ch); };
  }, [user]);

  // Helpline WhatsApp for rejection-help CTA
  useEffect(() => {
    setSupportWa({ number: "8801891921404", label: "Helpline" });
  }, []);

  // One-time B2B VIP due reminder on entry (only once per session, VIP only)
  useEffect(() => {
    if (!orders || !profile || userRoles.length === 0) return;
    if (!isVIP) return;
    const totalDue = orders.reduce((sum, o) => {
      const total = Number(o.total_price ?? 0);
      const paid = Number(o.amount ?? 0);
      return sum + Math.max(0, total - paid);
    }, 0);
    if (totalDue <= 0) return;
    const shownKey = `otv_b2b_due_shown_${user?.id}`;
    if (sessionStorage.getItem(shownKey)) return;
    setShowDueReminder(true);
    sessionStorage.setItem(shownKey, "1");
  }, [orders, profile, userRoles, isVIP, user?.id]);

  async function signOut() {
    await supabase.auth.signOut();
    toast.success(t("dashboard.signedOut"));
    navigate({ to: "/" });
  }

  if (authLoading || !user) {
    return <SiteShell><div className="container-page py-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></SiteShell>;
  }

  const activeOrders = orders?.filter((o) => !o.user_deleted_at) ?? null;
  const trashedOrders = orders?.filter((o) => !!o.user_deleted_at) ?? [];

  const stats = {
    total: activeOrders?.length ?? 0,
    pending: activeOrders?.filter((o) => o.status === "pending").length ?? 0,
    processing: activeOrders?.filter((o) => o.status === "processing").length ?? 0,
    completed: activeOrders?.filter((o) => o.status === "completed").length ?? 0,
  };

  const totalDue = activeOrders?.reduce((sum, o) => {
    const total = Number(o.total_price ?? 0);
    const paid = Number(o.amount ?? 0);
    return sum + Math.max(0, total - paid);
  }, 0) ?? 0;

  async function handleSoftDelete(orderId: string) {
    const ok = window.confirm("এই অর্ডারটি ট্র্যাশে সরানো হবে। ৩০ দিন পর স্থায়ীভাবে মুছে যাবে। চালিয়ে যাবেন?");
    if (!ok) return;
    try {
      await softDeleteOrder({ data: { orderId } });
      setOrders((prev) => prev?.map((o) => o.id === orderId ? { ...o, user_deleted_at: new Date().toISOString() } : o) ?? prev);
      toast.success("ট্র্যাশে সরানো হয়েছে — ৩০ দিনের মধ্যে রিস্টোর করা যাবে।");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to delete");
    }
  }

  async function handleRestore(orderId: string) {
    try {
      await restoreOrder({ data: { orderId } });
      setOrders((prev) => prev?.map((o) => o.id === orderId ? { ...o, user_deleted_at: null } : o) ?? prev);
      toast.success("রিস্টোর হয়েছে।");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to restore");
    }
  }

  return (
    <SiteShell>
      <Dialog open={showBlockedPopup} onOpenChange={setShowBlockedPopup}>
        <DialogContent className="max-w-md sm:max-w-lg text-center border-red-300 shadow-2xl">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-3 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <Ban className="h-8 w-8 text-red-600" />
            </div>
            <DialogTitle className="text-xl md:text-2xl font-bold text-red-700">
              আপনার অ্যাকাউন্ট ব্লক করা হয়েছে
            </DialogTitle>
            <DialogDescription className="sr-only">Account blocked notice</DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <p className="text-base md:text-lg font-semibold text-foreground leading-relaxed">
              প্রিয় ব্যবহারকারী, দুঃখিত! কিছু কারণে আপনার অ্যাকাউন্টটি সাময়িকভাবে ব্লক করা হয়েছে।
              আপনি এই মুহূর্তে কোনো নতুন অর্ডার দিতে পারবেন না, তবে আপনার পূর্বের সব তথ্য এবং অর্ডার দেখতে পারবেন।
            </p>
            {profile?.blocked_reason && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                <p className="font-semibold text-xs uppercase tracking-wider mb-1">কারণ</p>
                <p>{profile.blocked_reason}</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              আনব্লক করতে অনুগ্রহ করে আমাদের সাপোর্ট টিমের সাথে যোগাযোগ করুন।
            </p>
          </div>
          <DialogFooter className="flex justify-center sm:justify-center gap-2">
            <Button asChild className="bg-emerald-600 text-white hover:bg-emerald-700">
              <a href="https://wa.me/8801891921404" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Support
              </a>
            </Button>
            <Button variant="outline" onClick={() => setShowBlockedPopup(false)}>
              বন্ধ করুন
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDueReminder} onOpenChange={setShowDueReminder}>
        <DialogContent className="max-w-md sm:max-w-lg text-center border-red-200 shadow-2xl">
          <DialogHeader className="text-center">
            <DialogTitle className="text-xl md:text-2xl font-bold text-red-700">
              বকেয়া পেমেন্ট রিমাইন্ডার
            </DialogTitle>
            <DialogDescription className="sr-only">B2B due payment reminder</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              প্রিয় ক্লায়েন্ট, আপনার পূর্বের অর্ডারের{" "}
              <span className="text-red-600 font-bold">৳ {totalDue.toLocaleString()}</span>{" "}
              টাকা বকেয়া রয়েছে। দয়া করে পেমেন্টটি আপডেট করুন।
            </p>
          </div>
          <DialogFooter className="flex justify-center sm:justify-center">
            <Button
              onClick={() => setShowDueReminder(false)}
              className="bg-red-600 text-white hover:bg-red-700 shadow-lg"
            >
              বন্ধ করুন
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDownloadSuccess} onOpenChange={setShowDownloadSuccess}>
        <DialogContent className="max-w-md sm:max-w-lg text-center border-emerald-200 shadow-2xl">
          <DialogHeader className="text-center">
            <DialogTitle className="text-xl md:text-2xl font-bold text-emerald-700">
              অভিনন্দন! 🎉
            </DialogTitle>
            <DialogDescription className="sr-only">Download success message</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-lg md:text-xl font-semibold text-foreground leading-relaxed">
              আপনার ফাইলটি সফলভাবে ডাউনলোড হয়েছে। আমাদের ওপর আস্থা রাখার জন্য আপনাকে অসংখ্য ধন্যবাদ! আপনার সাথে আবার কাজ করার অপেক্ষায় রইলাম। ভালো থাকবেন! ✨
            </p>
          </div>
          <DialogFooter className="flex justify-center sm:justify-center">
            <Button
              onClick={() => setShowDownloadSuccess(false)}
              className="bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg"
            >
              ধন্যবাদ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="pt-10 md:pt-14 pb-20">
        <div className="container-page">
          {/* Premium dashboard header with watermark tax/VAT icons */}
          <div className="relative overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/10 via-background to-background p-6 md:p-8 shadow-card">
            {/* Decorative watermark icons */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 select-none text-primary/[0.07] dark:text-primary/[0.10]">
              <FileText className="absolute -top-3 right-[6%] h-24 w-24 -rotate-12" />
              <Receipt className="absolute top-8 right-[26%] h-16 w-16 rotate-6" />
              <Calculator className="absolute -bottom-4 right-[14%] h-24 w-24 -rotate-6" />
              <ShieldCheck className="absolute top-1/2 right-[44%] h-20 w-20 -translate-y-1/2 rotate-6" />
              <BadgeCheck className="absolute -bottom-3 right-[36%] h-16 w-16 rotate-12" />
              <Coins className="absolute top-4 right-[60%] h-14 w-14 -rotate-12" />
              <Landmark className="absolute -bottom-2 right-[55%] h-20 w-20 rotate-3" />
              <Banknote className="absolute top-1/2 right-[72%] h-16 w-16 -translate-y-1/2 -rotate-6" />
              <Stamp className="absolute top-10 right-[88%] h-14 w-14 rotate-12" />
            </div>
            {/* Soft sheen */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background/60 via-background/20 to-transparent" />

            <div className="relative flex flex-wrap items-end justify-between gap-4">
              <div className="min-w-0">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs font-semibold text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  {t("dashboard.eyebrow")}
                </span>
                <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold leading-tight">
                  {t("dashboard.welcome")}<span className="gradient-text">{profile?.full_name?.trim() || t("dashboard.fallbackName")}</span>
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground flex items-center gap-1.5">
                  <BadgeCheck className="h-3.5 w-3.5 text-primary/70" />
                  {user.email}
                </p>
                {profile?.referral_code && (
                  <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-background/80 backdrop-blur-sm pl-4 pr-1.5 py-1.5 shadow-sm">
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Referral</span>
                    <code className="font-mono font-bold text-primary tracking-widest text-sm">{profile.referral_code}</code>
                    <button
                      type="button"
                      onClick={async () => {
                        try { await navigator.clipboard.writeText(profile.referral_code); toast.success("Referral code copied"); }
                        catch { toast.error("Could not copy"); }
                      }}
                      className="ml-1 h-7 w-7 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground hover:opacity-90 transition"
                      aria-label="Copy referral code"
                      title="Copy referral code"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex gap-2 items-center">
                {isBlocked && (
                  <button
                    type="button"
                    onClick={() => setShowBlockedPopup(true)}
                    title="Your account is blocked — tap for details"
                    aria-label="Account blocked"
                    className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-red-100 text-red-600 border border-red-300 hover:bg-red-200 transition shadow-sm animate-pulse"
                  >
                    <Ban className="h-4 w-4" />
                  </button>
                )}
                {isBlocked ? (
                  <Button
                    disabled
                    className="opacity-60 cursor-not-allowed bg-muted text-muted-foreground"
                    title="Blocked accounts cannot place new orders"
                  >
                    <Ban className="h-4 w-4 mr-1.5" /> {t("dashboard.newOrder")}
                  </Button>
                ) : (
                  <Button asChild className="gradient-primary text-primary-foreground shadow-glow">
                    <Link to="/services"><UploadIcon className="h-4 w-4 mr-1.5" /> {t("dashboard.newOrder")}</Link>
                  </Button>
                )}
                <Button variant="outline" onClick={signOut} className="bg-background/70 backdrop-blur-sm"><LogOut className="h-4 w-4 mr-1.5" /> {t("dashboard.signOut")}</Button>
              </div>
            </div>
          </div>

          {isB2B && (
            <div className="mt-6 space-y-4">
              <CommissionPayoutCard />
              <CommissionHistoryTable />
            </div>
          )}




          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: t("dashboard.stats.total"), value: stats.total, icon: Receipt },
              { label: t("dashboard.stats.pending"), value: stats.pending, icon: Clock },
              { label: t("dashboard.stats.processing"), value: stats.processing, icon: Loader2 },
              { label: t("dashboard.stats.completed"), value: stats.completed, icon: CheckCircle2 },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="glass rounded-2xl p-5 shadow-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</p>
                    <p className="font-display text-3xl font-bold mt-1">{s.value}</p>
                  </div>
                  <div className="h-11 w-11 rounded-xl gradient-primary text-primary-foreground flex items-center justify-center shadow-glow">
                    <s.icon className="h-5 w-5" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10">
            <Tabs defaultValue="orders" className="w-full">
              <TabsList className="grid w-full max-w-xl grid-cols-3">
                <TabsTrigger value="orders"><Receipt className="h-4 w-4 mr-1.5" /> {t("dashboard.tabs.orders")}</TabsTrigger>
                <TabsTrigger value="payments"><Wallet className="h-4 w-4 mr-1.5" /> {t("dashboard.tabs.payments")}</TabsTrigger>
                <TabsTrigger value="profile"><UserCog className="h-4 w-4 mr-1.5" /> {t("dashboard.tabs.profile")}</TabsTrigger>
              </TabsList>

              <TabsContent value="orders" className="mt-6">
                <SectionHeading eyebrow={t("dashboard.orders.eyebrow")} title={<>{t("dashboard.orders.title1")}<span className="gradient-text">{t("dashboard.orders.title2")}</span></>} description={t("dashboard.orders.desc")} />

                {activeOrders === null ? (
                  <div className="mt-10 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
                ) : activeOrders.length === 0 ? (
                  <div className="mt-8 glass-strong rounded-3xl p-10 text-center">
                    <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><PackageIcon className="h-7 w-7" /></div>
                    <h3 className="mt-4 font-display text-xl font-bold">{t("dashboard.orders.emptyTitle")}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.orders.emptyDesc")}</p>
                    <Button asChild className="mt-5 gradient-primary text-primary-foreground"><Link to="/services">{t("dashboard.orders.browseServices")}</Link></Button>
                  </div>
                ) : (
                  <div className="mt-6 space-y-4">
                    {activeOrders.map((o, i) => {
                      const meta = STATUS_META[o.status] ?? STATUS_META.pending;
                      const Icon = meta.icon;
                      const paid = o.payment_status === "verified";
                      return (
                        <motion.div key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                          className="glass rounded-2xl p-5 md:p-6 shadow-card relative">
                          <div className="flex flex-wrap justify-between items-start gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted">{o.reference_id}</span>
                                <Badge variant="outline" className={meta.cls}>
                                  <Icon className={`h-3 w-3 mr-1 ${o.status === "processing" ? "animate-spin" : ""}`} /> {t(`dashboard.status.${meta.key}`)}
                                </Badge>
                                <Badge variant="outline" className={paid ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-amber-500/10 text-amber-600 border-amber-500/30"}>
                                  {paid ? t("dashboard.status.paymentVerified") : t("dashboard.status.paymentPending")}
                                </Badge>
                              </div>
                              <h3 className="mt-2 font-display font-bold text-lg truncate">{o.service_title}</h3>
                              <p className="text-sm text-muted-foreground">{o.package_name} · {new Date(o.created_at).toLocaleDateString()}</p>
                            </div>
                            {paid && (
                              <motion.div
                                initial={{ scale: 0, rotate: -25, opacity: 0 }}
                                animate={{ scale: 1, rotate: -14, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 180, damping: 14 }}
                                className="hidden sm:flex shrink-0 self-center pointer-events-none"
                                aria-label="Paid"
                              >
                                <div className="relative h-24 w-24 rounded-full flex items-center justify-center">
                                  {/* outer glow */}
                                  <div className="absolute inset-0 rounded-full bg-emerald-400/30 blur-xl" />
                                  {/* rotating dashed ring */}
                                  <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ repeat: Infinity, duration: 18, ease: "linear" }}
                                    className="absolute inset-0 rounded-full border-2 border-dashed border-emerald-500/50"
                                  />
                                  {/* main seal */}
                                  <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-700 text-white shadow-[0_10px_30px_-8px_rgba(16,185,129,0.7),inset_0_2px_6px_rgba(255,255,255,0.4),inset_0_-3px_8px_rgba(0,0,0,0.25)] ring-2 ring-white/90 flex flex-col items-center justify-center font-display font-extrabold border border-emerald-900/20">
                                    <CheckCircle2 className="h-5 w-5 drop-shadow-md" strokeWidth={2.5} />
                                    <span className="text-xs leading-none mt-1 tracking-[0.2em] drop-shadow">PAID</span>
                                    <span className="text-[9px] leading-none mt-1 opacity-95 tracking-wider">পেইড</span>
                                  </div>
                                  {/* shine */}
                                  <div className="absolute top-2 left-3 h-4 w-6 rounded-full bg-white/40 blur-sm rotate-[-20deg] pointer-events-none" />
                                </div>
                              </motion.div>
                            )}
                            <div className="text-right relative">
                              {/* mobile inline PAID badge */}
                              {paid && (
                                <div className="sm:hidden inline-flex items-center gap-1 mb-1 px-2 py-0.5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 text-white text-[10px] font-bold tracking-widest shadow">
                                  <CheckCircle2 className="h-3 w-3" /> PAID
                                </div>
                              )}
                              <p className="font-display font-bold text-2xl gradient-text">৳ {Number(o.total_price ?? o.amount ?? 0).toLocaleString()}</p>


                              {(() => {
                                const total = Number(o.total_price ?? 0);
                                const paid = Number(o.amount ?? 0);
                                const due = Math.max(0, total - paid);
                                if (total <= 0) return null;
                                return (
                                  <div className="mt-1 space-y-0.5 text-xs">
                                    <p className="text-emerald-600 font-semibold">পরিশোধিত: ৳ {paid.toLocaleString()}</p>
                                    {due > 0 ? (
                                      <>
                                        <p className="text-red-600 font-bold text-sm">বকেয়া: ৳ {due.toLocaleString()}</p>
                                        {isB2B && (
                                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[10px] font-bold tracking-wide">
                                            🔒 Pending Invoice — Locked
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <p className="text-emerald-600 font-semibold">বকেয়া নেই ✓</p>
                                    )}
                                  </div>
                                );
                              })()}
                              {o.payment_method && <p className="text-xs text-muted-foreground mt-1">{t("dashboard.orders.via")} {o.payment_method}</p>}
                            </div>
                          </div>

                          {o.payment_status === "rejected" && (
                            <div className="mt-4 rounded-xl border-2 border-red-300 bg-gradient-to-r from-red-50 to-rose-50 p-4 shadow-sm">
                              <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                                <div className="h-9 w-9 shrink-0 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                                  <XCircle className="h-5 w-5" />
                                </div>
                                <div className="min-w-0 flex-1 text-center">
                                  <p className="text-xs uppercase tracking-wider text-red-600 font-bold">
                                    📩 পেমেন্ট রিজেক্ট নোটিফিকেশন
                                  </p>
                                  <p className="mt-1 font-semibold text-red-800 leading-snug">
                                    {rejectionMsgs[o.id]?.title ?? "আপনার পেমেন্টটি রিজেক্ট হয়েছে"}
                                  </p>
                                  {rejectionMsgs[o.id]?.body && (
                                    <p className="mt-1.5 text-sm text-red-700 whitespace-pre-wrap leading-relaxed">
                                      {rejectionMsgs[o.id]!.body}
                                    </p>
                                  )}
                                  {rejectionMsgs[o.id]?.created_at && (
                                    <p className="mt-2 text-[11px] text-red-500/80">
                                      {new Date(rejectionMsgs[o.id]!.created_at).toLocaleString("bn-BD")}
                                    </p>
                                  )}
                                </div>
                                {supportWa && (
                                  <a
                                    href={`https://wa.me/${supportWa.number.replace(/\D/g, "")}?text=${encodeURIComponent(`আসসালামু আলাইকুম, আমার অর্ডার #${o.reference_id} এর পেমেন্ট রিজেক্ট হয়েছে। অনুগ্রহ করে সাহায্য করুন।`)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold shadow-sm transition-colors"
                                    aria-label="WhatsApp support"
                                  >
                                    <MessageCircle className="h-4 w-4" />
                                    <span>WhatsApp</span>
                                  </a>
                                )}
                              </div>
                            </div>
                          )}

                          <OrderProgressBar
                            status={o.status}
                            paymentVerified={paid}
                            hasDocuments={(o.document_urls?.length ?? 0) > 0}
                            hasDelivery={Boolean((o.delivery_url ?? "").trim())}
                          />

                          {(() => {
                            const isCustomOrder = (o as any).package_slug === "custom" || (o as any).payment_status === "pending_quote";
                            if (!isCustomOrder) return null;
                            const totalP = Number(o.total_price ?? 0);
                            const paidP = Number(o.amount ?? 0);
                            const dueP = Math.max(0, totalP - paidP);
                            const awaiting = totalP <= 0;
                            return (
                              <div className={`mt-4 rounded-xl border p-3 text-sm flex items-start gap-2 ${awaiting ? "border-amber-200 bg-amber-50 text-amber-800" : dueP > 0 ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                                <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
                                <div className="min-w-0">
                                  <p className="font-semibold">কাস্টম / ম্যানুয়াল অর্ডার</p>
                                  {awaiting ? (
                                    <p className="mt-0.5 text-xs">এডমিন আপনার অর্ডার রিভিউ করছেন। শীঘ্রই পেমেন্ট অ্যামাউন্ট সেট করা হবে — এরপর আপনি পেমেন্ট সম্পন্ন করে ফাইল ডাউনলোড করতে পারবেন।</p>
                                  ) : dueP > 0 ? (
                                    <p className="mt-0.5 text-xs">এডমিন কর্তৃক নির্ধারিত পেমেন্ট: <span className="font-bold">৳ {totalP.toLocaleString()}</span> · বকেয়া: <span className="font-bold">৳ {dueP.toLocaleString()}</span> — পেমেন্ট সম্পন্ন করুন।</p>
                                  ) : (
                                    <p className="mt-0.5 text-xs">পেমেন্ট সম্পন্ন হয়েছে ✅</p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}


                          {o.document_urls?.length > 0 && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {t("dashboard.orders.documents")} ({o.document_urls.length})</p>
                              <div className="flex flex-wrap gap-2">
                                {o.document_urls.map((u, idx) => (
                                  <a key={idx} href={u} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20">
                                    <FileText className="h-3.5 w-3.5" /> {t("dashboard.orders.document")} {idx + 1} <ExternalLink className="h-3 w-3" />
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}
                          {(!o.document_urls || o.document_urls.length === 0) && Array.isArray((o as any).document_snapshot) && (o as any).document_snapshot.length > 0 && (
                            <div className="mt-4 pt-4 border-t opacity-90">
                              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                                <FileText className="h-3.5 w-3.5" /> Submitted Documents ({(o as any).document_snapshot.length}) <span className="ml-1 text-[10px] normal-case tracking-normal text-muted-foreground/80">· archived</span>
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {(o as any).document_snapshot.map((d: any, idx: number) => (
                                  <span key={idx} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-muted text-muted-foreground border">
                                    <FileText className="h-3.5 w-3.5" /> {d?.name ?? `Document ${idx + 1}`}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {(() => {
                            const deliveredFiles = (o.delivery_url ?? "")
                              .split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
                            if (deliveredFiles.length === 0) return null;
                            const totalP = Number(o.total_price ?? 0);
                            const paidP = Number(o.amount ?? 0);
                            const dueP = Math.max(0, totalP - paidP);
                            const isCustomOrder = (o as any).package_slug === "custom" || (o as any).payment_status === "pending_quote";
                            const awaitingQuote = isCustomOrder && totalP <= 0;
                            // Access rules:
                            //  - B2B / VIP: full download access on every delivered file
                            //    (their package balance is tracked separately and blocks
                            //    new orders, but does not lock the file they already received).
                            //  - Regular: locked when due > 0 OR awaiting admin quote
                            const regularLocked = !isB2B && (dueP > 0 || awaitingQuote);
                            const locked = regularLocked;
                            const downloadOnlyLocked = false;
                            const lockedMsg = awaitingQuote
                              ? "এই কাস্টম অর্ডারের জন্য এডমিন এখনো পেমেন্ট অ্যামাউন্ট সেট করেননি। সেট হলে পেমেন্ট করে ফাইল ডাউনলোড করতে পারবেন।"
                              : "আপনার বকেয়া টাকা পরিশোধ করে ফাইলটি ডাউনলোড করুন।";
                            const b2bMsg = "";
                            return (
                              <div className="mt-4 pt-4 border-t">
                                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                  <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                    <FolderOpen className="h-3.5 w-3.5 text-emerald-600" />
                                    {t("dashboard.orders.deliveredFiles")} ({deliveredFiles.length})
                                    {o.delivered_at && <span className="ml-2 normal-case tracking-normal text-[11px] text-muted-foreground/80">· {new Date(o.delivered_at).toLocaleDateString()}</span>}
                                  </p>
                                  {awaitingQuote && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                                      ⏳ এডমিন কোটের জন্য অপেক্ষমান
                                    </span>
                                  )}
                                  {!awaitingQuote && locked && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-full">
                                      🔒 Payment Required · বকেয়া: ৳ {dueP.toLocaleString()}
                                    </span>
                                  )}
                                  {downloadOnlyLocked && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-1 rounded-full">
                                      👁 B2B View-Only
                                    </span>
                                  )}
                                </div>
                                {locked && (
                                  <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                    <span>{lockedMsg}</span>
                                  </div>
                                )}
                                {downloadOnlyLocked && (
                                  <div className="mb-3 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-700 flex items-start gap-2">
                                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                                    <span>{b2bMsg}</span>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                  {deliveredFiles.map((u, idx) => {
                                    const name = filenameFromUrl(u, `file-${idx + 1}`);
                                    return (
                                      <div key={idx} className={`group relative rounded-xl border bg-background/60 p-3 flex flex-col items-center text-center transition-all ${locked ? "opacity-60" : "hover:border-primary/50 hover:shadow-card"}`}>
                                        <div className="h-12 w-12 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center mb-2">
                                          <FileText className="h-6 w-6" />
                                        </div>
                                        <p className="text-xs font-medium truncate w-full" title={name}>{name}</p>
                                        <div className="mt-2 flex gap-1.5 w-full">
                                          {locked ? (
                                            <>
                                              <button
                                                type="button"
                                                onClick={() => toast.error(lockedMsg)}
                                                className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground cursor-not-allowed"
                                              >
                                                <ExternalLink className="h-3 w-3" /> {t("dashboard.orders.view")}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => toast.error(lockedMsg)}
                                                className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-red-100 text-red-700 cursor-not-allowed"
                                              >
                                                🔒 {t("dashboard.orders.download")}
                                              </button>
                                            </>
                                          ) : downloadOnlyLocked ? (
                                            <>
                                              <a href={u} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-muted hover:bg-muted/70">
                                                <ExternalLink className="h-3 w-3" /> {t("dashboard.orders.view")}
                                              </a>
                                              <button
                                                type="button"
                                                onClick={() => toast.error(b2bMsg)}
                                                className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-indigo-100 text-indigo-700 cursor-not-allowed"
                                                title="B2B accounts cannot download files"
                                              >
                                                🔒 {t("dashboard.orders.download")}
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              <a href={u} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-muted hover:bg-muted/70">
                                                <ExternalLink className="h-3 w-3" /> {t("dashboard.orders.view")}
                                              </a>
                                              <a
                                                href={u} download={name}
                                                onClick={() => {
                                                  confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
                                                  setShowDownloadSuccess(true);
                                                }}
                                                className="flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20"
                                              >
                                                <Download className="h-3 w-3" /> {t("dashboard.orders.download")}
                                              </a>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {o.delivery_notes && (
                                  <p className="mt-3 text-xs text-muted-foreground whitespace-pre-wrap bg-muted/40 rounded-lg p-2.5">{o.delivery_notes}</p>
                                )}
                              </div>
                            );
                          })()}



                          <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-3 justify-between">
                            {!paid ? (
                              o.payment_status === "rejected" ? (
                                <>
                                  <p className="text-sm text-red-600 flex items-center gap-1.5 font-semibold">
                                    <XCircle className="h-4 w-4" /> পেমেন্ট রিজেক্ট হয়েছে — অনুগ্রহ করে আবার পেমেন্ট জমা দিন।
                                  </p>
                                  <Button asChild size="sm" className="bg-red-600 text-white hover:bg-red-700 shadow-md">
                                    <Link to="/payment" search={{ order: o.id } as any}>
                                      <AlertCircle className="h-4 w-4 mr-1.5" /> Retry Payment
                                    </Link>
                                  </Button>
                                </>
                              ) : o.payment_status === "pending_verification" ? (
                                <>
                                  <p className="text-sm text-amber-600 flex items-center gap-1.5"><Clock className="h-4 w-4" /> পেমেন্ট ভেরিফিকেশনের অপেক্ষায়।</p>
                                  <Button asChild size="sm" variant="outline">
                                    <Link to="/payment" search={{ order: o.id } as any}>Update Payment</Link>
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm text-muted-foreground flex items-center gap-1.5"><AlertCircle className="h-4 w-4 text-amber-500" /> {t("dashboard.orders.completePaymentMsg")}</p>
                                  <Button asChild size="sm" className="gradient-primary text-primary-foreground">
                                    <Link to="/payment" search={{ order: o.id } as any}>{t("dashboard.orders.completePayment")}</Link>
                                  </Button>
                                </>
                              )
                            ) : <span />}
                            <div className="flex items-center gap-2 flex-wrap">
                              <CompactReceiptDialog order={o as any} label="রসিদ" />
                              <InvoiceDownloadButton order={o as any} isB2B={isB2B} />
                              {(o as any).invoice_url && <UploadedInvoiceBtn order={o as any} />}
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => handleSoftDelete(o.id)}
                                className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                                title="ট্র্যাশে সরান (৩০ দিন পর স্থায়ীভাবে মুছে যাবে)"
                              >
                                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}

                    {trashedOrders.length > 0 && (
                      <div className="mt-10">
                        <div className="flex items-center gap-2 mb-3">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                          <h3 className="font-display font-bold text-base">ট্র্যাশ ({trashedOrders.length})</h3>
                          <span className="text-xs text-muted-foreground">— ৩০ দিন পর স্থায়ীভাবে মুছে যাবে</span>
                        </div>
                        <div className="space-y-3">
                          {trashedOrders.map((o) => {
                            const deletedAt = o.user_deleted_at ? new Date(o.user_deleted_at) : null;
                            const daysLeft = deletedAt
                              ? Math.max(0, 30 - Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24)))
                              : 30;
                            return (
                              <div key={o.id} className="rounded-2xl border border-red-200/60 bg-red-50/40 dark:bg-red-950/10 p-4 flex flex-wrap items-center justify-between gap-3 opacity-90">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted">{o.reference_id}</span>
                                    <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">
                                      <Trash2 className="h-3 w-3 mr-1" /> Moving to Trash — {daysLeft} দিন বাকি
                                    </Badge>
                                  </div>
                                  <p className="mt-1 text-sm font-medium truncate">{o.service_title} <span className="text-muted-foreground">· {o.package_name}</span></p>
                                  {deletedAt && (
                                    <p className="text-[11px] text-muted-foreground mt-0.5">Will be permanently deleted on {new Date(deletedAt.getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}</p>
                                  )}
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleRestore(o.id)}
                                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                                >
                                  <Undo2 className="h-4 w-4 mr-1.5" /> Restore
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="payments" className="mt-6">
                <PaymentsTab orders={orders} />
              </TabsContent>

              <TabsContent value="profile" className="mt-6">
                <ProfileTab
                  userId={user.id}
                  email={user.email ?? ""}
                  profile={profile}
                  onSaved={(p) => setProfile(p)}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}

function UploadedInvoiceBtn({ order }: { order: any }) {
  const [busy, setBusy] = useState(false);
  const getSigned = useServerFn(getOrderInvoiceSignedUrl);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          const { url } = await getSigned({ data: { orderId: order.id } });
          const a = document.createElement("a");
          a.href = url;
          a.target = "_blank";
          a.rel = "noopener";
          a.download = `invoice-${order.reference_id || order.id.slice(0, 8)}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch (e: any) {
          toast.error(e?.message ?? "Download failed");
        } finally {
          setBusy(false);
        }
      }}
      className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      title="Admin-uploaded official invoice"
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      Download Invoice
    </Button>
  );
}

function InvoiceDownloadButton({ order, isB2B }: { order: any; isB2B?: boolean }) {
  const { t } = useTranslation();
  const { download, busyId } = useDownloadInvoice();
  const busy = busyId === order.id;
  if (isB2B) {
    return (
      <div className="flex flex-col gap-1.5">
        <InvoiceDownloadControl
          busy={busy}
          label="Full Invoice (Show Amount)"
          defaultShowFee={true}
          onDownload={async (opts) => {
            try { await download(order, { ...opts, showFee: true }); } catch (e: any) { toast.error(e?.message || t("dashboard.orders.downloadFailed")); }
          }}
        />
        <InvoiceDownloadControl
          busy={busy}
          label="Client Invoice (Hide Amount)"
          defaultShowFee={false}
          onDownload={async (opts) => {
            try { await download(order, { ...opts, showFee: false }); } catch (e: any) { toast.error(e?.message || t("dashboard.orders.downloadFailed")); }
          }}
        />
      </div>
    );
  }
  return (
    <InvoiceDownloadControl
      busy={busy}
      label={t("dashboard.orders.downloadInvoice")}
      onDownload={async (opts) => {
        try { await download(order, opts); } catch (e: any) { toast.error(e?.message || t("dashboard.orders.downloadFailed")); }
      }}
    />
  );
}


function PaymentsTab({ orders }: { orders: Order[] | null }) {
  const { t } = useTranslation();
  if (orders === null) {
    return <div className="mt-10 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }
  const payments = orders.filter((o) => o.payment_transaction_id || o.payment_method);
  const totalPaid = payments
    .filter((o) => o.payment_status === "verified")
    .reduce((sum, o) => sum + Number(o.amount ?? 0), 0);

  async function copyTrx(trx: string) {
    try { await navigator.clipboard.writeText(trx); toast.success(t("dashboard.payments.trxCopied")); }
    catch { toast.error(t("dashboard.payments.copyFailed")); }
  }

  return (
    <>
      <SectionHeading eyebrow={t("dashboard.payments.eyebrow")} title={<>{t("dashboard.payments.title1")}<span className="gradient-text">{t("dashboard.payments.title2")}</span></>} description={t("dashboard.payments.desc")} />

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 shadow-card">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("dashboard.payments.totalPayments")}</p>
          <p className="font-display text-3xl font-bold mt-1">{payments.length}</p>
        </div>
        <div className="glass rounded-2xl p-5 shadow-card">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("dashboard.payments.verifiedCount")}</p>
          <p className="font-display text-3xl font-bold mt-1">{payments.filter((p) => p.payment_status === "verified").length}</p>
        </div>
        <div className="glass rounded-2xl p-5 shadow-card">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("dashboard.payments.totalPaid")}</p>
          <p className="font-display text-3xl font-bold mt-1 gradient-text">৳ {totalPaid.toLocaleString()}</p>
        </div>
      </div>

      {payments.length === 0 ? (
        <div className="mt-8 glass-strong rounded-3xl p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><CreditCard className="h-7 w-7" /></div>
          <h3 className="mt-4 font-display text-xl font-bold">{t("dashboard.payments.emptyTitle")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("dashboard.payments.emptyDesc")}</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {payments.map((p, i) => {
            const verified = p.payment_status === "verified";
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl p-5 shadow-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono px-2 py-0.5 rounded bg-muted">{p.reference_id}</span>
                      <Badge variant="outline" className={verified ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-amber-500/10 text-amber-600 border-amber-500/30"}>
                        {verified ? <><CheckCircle2 className="h-3 w-3 mr-1" /> {t("dashboard.status.verified")}</> : <><Clock className="h-3 w-3 mr-1" /> {t("dashboard.status.pendingVerification")}</>}
                      </Badge>
                      {p.payment_method && (
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                          <Wallet className="h-3 w-3 mr-1" /> {p.payment_method}
                        </Badge>
                      )}
                    </div>
                    <h3 className="mt-2 font-display font-bold truncate">{p.service_title}</h3>
                    <p className="text-xs text-muted-foreground">{p.package_name} · {new Date(p.created_at).toLocaleString()}</p>
                    {p.payment_transaction_id && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">{t("dashboard.payments.trxId")}</span>
                        <code className="text-xs font-mono px-2 py-0.5 rounded bg-muted">{p.payment_transaction_id}</code>
                        <button onClick={() => copyTrx(p.payment_transaction_id!)} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                          <Copy className="h-3 w-3" /> {t("common.copy")}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-display font-bold text-xl gradient-text">৳ {Number(p.amount ?? 0).toLocaleString()}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </>
  );
}

function ProfileTab({
  userId,
  email,
  profile,
  onSaved,
}: {
  userId: string;
  email: string;
  profile: any;
  onSaved: (p: any) => void;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState<string>(profile?.full_name ?? "");
  const [phone, setPhone] = useState<string>(profile?.phone ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFullName(profile?.full_name ?? "");
    setPhone(profile?.phone ?? "");
  }, [profile]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    const name = fullName.trim();
    const ph = phone.trim();
    if (name.length < 2 || name.length > 100) {
      toast.error(t("dashboard.profile.invalidName"));
      return;
    }
    if (ph && !/^[+0-9\s\-]{6,20}$/.test(ph)) {
      toast.error(t("dashboard.profile.invalidPhone"));
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("profiles")
      .update({ full_name: name, phone: ph })
      .eq("id", userId)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    onSaved(data);
    toast.success(t("dashboard.profile.updated"));
  }

  return (
    <>
      <SectionHeading
        eyebrow={t("dashboard.profile.eyebrow")}
        title={<>{t("dashboard.profile.title1")}<span className="gradient-text">{t("dashboard.profile.title2")}</span></>}
        description={t("dashboard.profile.desc")}
      />
      <form
        onSubmit={onSave}
        className="mt-6 glass-strong rounded-3xl p-6 md:p-8 shadow-card max-w-2xl space-y-5"
      >
        <div>
          <Label htmlFor="p-name">{t("dashboard.profile.fullName")}</Label>
          <Input
            id="p-name"
            required
            maxLength={100}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="p-phone">{t("dashboard.profile.phone")}</Label>
          <Input
            id="p-phone"
            maxLength={20}
            placeholder={t("dashboard.profile.phonePlaceholder")}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="p-email">{t("dashboard.profile.email")}</Label>
          <Input id="p-email" value={email} disabled className="mt-1.5 opacity-70" />
          <p className="mt-1 text-[11px] text-muted-foreground">{t("dashboard.profile.emailHint")}</p>
        </div>
        {profile?.referral_code && (
          <div>
            <Label htmlFor="p-ref">Your Referral Code</Label>
            <div className="mt-1.5 flex gap-2">
              <Input
                id="p-ref"
                value={profile.referral_code}
                readOnly
                className="font-mono uppercase tracking-widest font-bold text-primary bg-primary/5 border-primary/30"
              />
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(profile.referral_code);
                    toast.success("Referral code copied");
                  } catch {
                    toast.error("Could not copy");
                  }
                }}
              >
                <Copy className="h-4 w-4 mr-1.5" /> Copy
              </Button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Share this code with friends — they can enter it when signing up.
            </p>
          </div>
        )}
        <div className="pt-2">
          <Button
            type="submit"
            disabled={saving}
            className="gradient-primary text-primary-foreground shadow-glow"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> {t("dashboard.profile.saving")}</> : <><Save className="h-4 w-4 mr-1.5" /> {t("dashboard.profile.saveChanges")}</>}
          </Button>
        </div>
      </form>
    </>
  );
}
